<template>
  <div v-click-outside="onOutside" class="form-group">
    <label v-if="label" :for="inputId" class="d-block mb-1">
      {{ label }}
      <span v-if="required" class="text-danger" aria-hidden="true">*</span>
    </label>

    <div class="position-relative">
      <input
        :id="inputId"
        ref="input"
        class="form-control"
        :class="{ 'is-invalid': hasError }"
        type="text"
        name="city"
        autocomplete="off"
        :placeholder="placeholder"
        :value="innerValue"
        role="combobox"
        :aria-expanded="open ? 'true' : 'false'"
        :aria-controls="listboxId"
        :aria-activedescendant="activeDescId"
        :disabled="disabled"
        @input="onInput"
        @keydown.down.prevent="move(1)"
        @keydown.up.prevent="move(-1)"
        @keydown.enter.prevent="onEnter"
        @focus="onFocus"
      />

      <!-- Spinner -->
      <div
        v-if="loading"
        class="position-absolute end-0 translate-middle-y pr-2"
        style="right: 0.5rem; top: 7px"
      >
        <div
          class="spinner-border spinner-border-sm"
          role="status"
          aria-hidden="true"
        ></div>
      </div>

      <!-- Suggestions -->
      <ul
        v-if="open && suggestions.length"
        :id="listboxId"
        class="dropdown-menu show w-100 mt-1 border"
        role="listbox"
        style="max-height: 18rem; overflow: auto"
      >
        <li
          v-for="(s, i) in suggestions"
          :id="optionId(i)"
          :key="s.placeId"
          role="option"
          :aria-selected="i === highlighted ? 'true' : 'false'"
        >
          <button
            type="button"
            class="dropdown-item text-left"
            :class="{ active: i === highlighted }"
            @mouseenter="highlighted = i"
            @mousedown.prevent="selectSuggestion(s)"
          >
            <div class="text-truncate">{{ s.text }}</div>
            <small
              v-if="s.secondary"
              class="text-muted text-truncate d-block"
              >{{ s.secondary }}</small
            >
          </button>
        </li>
      </ul>
    </div>

    <div v-if="hasError && invalidFeedback" class="invalid-feedback d-block">
      {{ invalidFeedback }}
    </div>
  </div>
</template>

<script>
import axios from "axios";
import vClickOutside from "v-click-outside";

export default {
  name: "PlacesAutocomplete",
  directives: {
    clickOutside: vClickOutside.directive
  },
  props: {
    /* Vue 2 v-model */
    value: { type: String, default: "" },
    id: { type: String, default: "city-input" }, // pass stable id to avoid hydration-style glitches
    label: { type: String, default: "" },
    placeholder: { type: String, default: "" },
    disabled: { type: Boolean, default: false },
    required: { type: Boolean, default: false },
    hasError: { type: Boolean, default: false },
    invalidFeedback: { type: String, default: "This field is required." },

    /* Backend options */
    countries: { type: Array, default: () => [] }, // ['CA','US']
    types: { type: Array, default: () => ["street_address"] },
    lang: { type: String, default: "en" },
    minLength: { type: Number, default: 3 },
    debounceMs: { type: Number, default: 600 },

    /**
     * Location bias for prioritizing results near a geographic point.
     * Default: Montreal, QC with 50km radius (Google Places API max).
     * Pass null to disable bias.
     */
    locationBias: {
      type: Object,
      default: () => ({
        lat: 45.5017,
        lng: -73.5673,
        radiusMeters: 50000
      })
    }
  },
  data() {
    return {
      innerValue: this.value || "",
      suggestions: [],
      open: false,
      loading: false,
      highlighted: -1,
      lastQuery: "",
      cancelSource: null,
      sessionToken: this.newToken()
    };
  },
  computed: {
    inputId() {
      return this.id;
    },
    listboxId() {
      return `${this.inputId}-listbox`;
    },
    activeDescId() {
      return this.highlighted >= 0
        ? this.optionId(this.highlighted)
        : undefined;
    }
  },
  watch: {
    value(v) {
      if (v !== this.innerValue) this.innerValue = v || "";
    }
  },
  methods: {
    optionId(i) {
      return `${this.listboxId}-opt-${i}`;
    },
    newToken() {
      try {
        if (window.crypto && window.crypto.randomUUID)
          return window.crypto.randomUUID();
      } catch (e) {
        // log error here
      }
      return (
        "st_" +
        Math.random()
          .toString(36)
          .slice(2)
      );
    },
    onOutside() {
      this.closeList();
    },
    openList() {
      if (!this.open) {
        this.open = true;
        this.$emit("opened");
      }
    },
    closeList() {
      if (this.open) {
        this.open = false;
        this.highlighted = -1;
        this.$emit("closed");
      }
    },
    onFocus() {
      if (this.suggestions.length) this.openList();
    },
    onInput(e) {
      const v = e.target.value;
      this.innerValue = v;
      this.$emit("input", v); // Vue 2 v-model
      if ((v || "").trim().length >= this.minLength) {
        this.queueFetch(v);
      } else {
        this.suggestions = [];
        this.closeList();
      }
    },
    move(delta) {
      if (!this.open) this.openList();
      const n = this.suggestions.length;
      if (!n) return;
      this.highlighted = (this.highlighted + delta + n) % n;
    },
    async onEnter() {
      if (
        this.open &&
        this.highlighted >= 0 &&
        this.highlighted < this.suggestions.length
      ) {
        await this.selectSuggestion(this.suggestions[this.highlighted]);
      }
    },

    /* Debounce */
    queueFetch: (function() {
      let t = null;
      return function(query) {
        if (t) clearTimeout(t);
        t = setTimeout(() => this.fetchSuggestions(query), this.debounceMs);
      };
    })(),

    /* Networking */
    cancelInFlight() {
      if (this.cancelSource) {
        this.cancelSource.cancel("New request supersedes previous one");
        this.cancelSource = null;
      }
    },

    async fetchSuggestions(query) {
      const q = (query || "").trim();
      if (q.length < this.minLength) return;
      if (q === this.lastQuery) {
        this.openList();
        return;
      }
      this.lastQuery = q;

      this.loading = true;
      this.cancelInFlight();
      this.cancelSource = axios.CancelToken.source();

      try {
        const body = {
          q,
          lang: this.lang,
          sessionToken: this.sessionToken,
          countries: this.countries,
          types: this.types,
          locationBias: this.locationBias
        };

        const { data } = await axios.post("/places/autocomplete", body, {
          cancelToken: this.cancelSource.token
        });
        const list = Array.isArray(data)
          ? data
          : data && data.suggestions
          ? data.suggestions
          : [];
        this.suggestions = list;
        if (list.length) {
          this.highlighted = 0;
          this.openList();
        } else {
          this.closeList();
        }
      } catch (err) {
        if (!axios.isCancel(err)) {
          // optional: show toast
        }
        this.suggestions = [];
        this.closeList();
      } finally {
        this.loading = false;
      }
    },

    async selectSuggestion(s) {
      this.innerValue = s.text;
      this.$emit("input", s.text);

      let details = null;
      try {
        this.loading = true;
        this.cancelInFlight();
        this.cancelSource = axios.CancelToken.source();

        const body = {
          place_id: s.placeId,
          sessionToken: this.sessionToken,
          lang: this.lang
        };

        const { data } = await axios.post("/places/details", body, {
          cancelToken: this.cancelSource.token
        });
        details = data || null;
      } catch (err) {
        if (!axios.isCancel(err)) {
          // optional: toast
        }
      } finally {
        this.loading = false;
        // rotate token after a completed selection (per Google guidance)
        this.sessionToken = this.newToken();
      }

      const lat =
        (details &&
          details.geometry &&
          (details.geometry.lat ||
            (details.geometry.location &&
              details.geometry.location.latitude))) ||
        undefined;
      const lng =
        (details &&
          details.geometry &&
          (details.geometry.lng ||
            (details.geometry.location &&
              details.geometry.location.longitude))) ||
        undefined;

      this.$emit("selected", {
        suggestion: s,
        details,
        lat: typeof lat === "number" ? lat : NaN,
        lng: typeof lng === "number" ? lng : NaN
      });

      this.suggestions = [];
      this.closeList();
      this.$refs.input && this.$refs.input.focus();
    }
  },
  beforeDestroy() {
    this.cancelInFlight();
  }
};
</script>
